import { useMemo } from 'react';
import { useMapStore } from '../../application/mapStore';
import { useFilterStore } from '../../../public-filters/application/filterStore';
import { buildPropertySearchRequest } from '../../application/mappers/buildPropertySearchRequest';
import { usePropertyViewerContext } from '../../../../contexts/PropertyViewer';
import { useLoginPrompt } from '../../../../components/common/LoginPromptContext';
import { PropertySearchRequest } from '../../domain/valueObjects/PropertySearchRequest';

// Assembles a PropertySearchRequest from map state, filters, and (for logged-in
// users) the active list sort from PropertyViewerContext. Shared by
// useFilteredProperties and useFilteredMarkers so the sentinel handling and
// auth gating can't drift between the two call sites.
//
// activeListSortId is either the UUID of a list sort or a "just browsing"
// sentinel. Two sentinels exist in the codebase: 'property' (per the comment at
// contexts/PropertyViewer.tsx:44) and 'properties' (set by
// components/property-list-viewer/PropertyList.tsx:83). Both are treated as no
// list sort.
export function usePropertySearchRequest(
  resultType: 'markers' | 'properties'
): PropertySearchRequest {
  const { bounds } = useMapStore();
  const { polygonFeatures: polygon, filter } = useFilterStore();
  const { activeListSortId } = usePropertyViewerContext();
  const { isLoggedIn } = useLoginPrompt();

  const effectiveListSortId =
    isLoggedIn &&
    activeListSortId &&
    activeListSortId !== 'property' &&
    activeListSortId !== 'properties'
      ? activeListSortId
      : undefined;

  return useMemo(
    () =>
      buildPropertySearchRequest({
        bounds,
        polygon,
        filter,
        resultType,
        listSortId: effectiveListSortId,
      }),
    [bounds, polygon, filter, resultType, effectiveListSortId]
  );
}
